import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserNotesByChapter } from '../store/notesSlice';
import { fetchHighlightsByChapter } from '../store/highlightSlice';
import { makeChapterAnnotationKey, makeHighlightChapterKey } from '../utils/annotationKeys';

export const usePassageAnnotations = ({ verses = [], version, user, refreshKey = 0 }) => {
  const dispatch = useDispatch();
  const notesByChapter = useSelector((state) => state.notes?.byChapter || {});
  const highlightsByChapter = useSelector((state) => state.highlight?.highlights || {});
  const groups = useMemo(() => {
    const unique = new Map();
    verses.forEach((verse) => {
      const book = verse.book;
      const chapter = verse.chapter;
      if (book && chapter) unique.set(makeHighlightChapterKey(version, book, chapter), { book, chapter });
    });
    return [...unique.entries()];
  }, [verses, version]);

  useEffect(() => {
    if (!user || !groups.length) return;
    groups.forEach(([chapterKey, { book, chapter }]) => {
      const notesKey = makeChapterAnnotationKey(version, book, chapter);
      if (!notesByChapter[notesKey]) dispatch(fetchUserNotesByChapter({ book, chapter, version }));
      if (!highlightsByChapter[chapterKey]) dispatch(fetchHighlightsByChapter({ book, chapter, version }));
    });
  }, [dispatch, groups, highlightsByChapter, notesByChapter, refreshKey, user, version]);

  const getMemo = useCallback((verseId) => {
    for (const [chapterKey, notes] of Object.entries(notesByChapter)) {
      if (chapterKey.startsWith('all:')) {
        const note = notes.find((item) => Number(item.verse_id) === Number(verseId));
        if (note) return note;
      }
    }
    return null;
  }, [notesByChapter]);

  const getHighlights = useCallback((verseId) => {
    const output = [];
    const verse = verses.find((item) => Number(item.verse_id) === Number(verseId));
    const chapterKey = verse
      ? makeHighlightChapterKey(version, verse.book, verse.chapter)
      : null;
    Object.entries(highlightsByChapter).forEach(([cachedChapterKey, highlights]) => {
      if (cachedChapterKey === chapterKey) {
        output.push(...highlights.filter((item) => Number(item.verse_id) === Number(verseId)));
      }
    });
    return output;
  }, [highlightsByChapter, version, verses]);

  return { getMemo, getHighlights, groups };
};
